import { NgModule, Type, ModuleWithProviders, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ZrSwitchModule } from 'zr-switch';
import { ZrCounterModule } from 'zr-counter';
import { NgxQueryModule } from '@zhongruigroup/ngx-query';
import { BsDatepickerModule } from 'ngx-bootstrap';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { SweetAlert2Module } from '@toverux/ngx-sweetalert2';
import { SelectModule } from 'ng-select';

import { DragDirective } from './directive/drag.directive';
import { ControlValidateDirective } from './directive/control-validate.directive';
import { FloatDigitOnlyDirective } from './directive/float-digit-only.directive';
import { DigitOnlyDirective } from './directive/digit-only.directive';
import { ControlIdDirective } from './directive/control-id.directive';
import { ElementClassDirective } from './directive/element-class.directive';
import { LayoutHostDirective } from './components/layout-host.directive';

import { DynamicFormDesignerComponent } from './dynamic-form-designer.component';
import { LayoutDesignerComponent } from './components/layout-designer/layout-designer.component';
import { DynamicFormComponent } from './dynamic-form/dynamic-form/dynamic-form.component';
import { DesignHostComponent } from './components/design-host/design-host.component';
import { PropertyGridComponent } from './components/property-grid/property-grid.component';
import { PropertyGroupComponent } from './components/property-group/property-group.component';
import { ToolBoxComponent } from './components/tool-box/tool-box.component';
import { ToolBoxGroupComponent } from './components/tool-box-group/tool-box-group.component';
import { ToolBoxControlComponent } from './components/tool-box-control/tool-box-control.component';
import { ControlValidateLabelComponent } from './components/control-validate-label/control-validate-label.component';
import { EventGroupComponent } from './components/event-group/event-group.component';
import { ValidatorGroupComponent } from './components/validator-group/validator-group.component';
import { FormEventGroupComponent } from './components/form-event-group/form-event-group.component';
import { DragBoxComponent } from './components/drag-box/drag-box.component';
import { ValidateMessageComponent } from './components/validate-message/validate-message.component';

import { AbstractControlContainerComponent } from './controls/abstract-control-container/abstract-control-container.component';
import { LabelComponent } from './controls/label/label.component';
import { TextBoxComponent } from './controls/text-box/text-box.component';
import { ContainerComponent } from './controls/container/container.component';
import { RowComponent } from './controls/row/row.component';
import { CellComponent } from './controls/cell/cell.component';
import { DatetimeComponent } from './controls/datetime/datetime.component';
import { MultiLineTextBoxComponent } from './controls/multi-line-text-box/multi-line-text-box.component';
import { RadioComponent } from './controls/radio/radio.component';
import { MultiCheckBoxComponent } from './controls/multi-check-box/multi-check-box.component';
import { DropDownComponent } from './controls/drop-down/drop-down.component';
import { OptionsControlBaseComponent } from './controls/options-control-base/options-control-base.component';
import { DigitalComponent } from './controls/digital/digital.component';
import { ParagraphComponent } from './controls/paragraph/paragraph.component';
import { EmailComponent } from './controls/email/email.component';
import { SwitchComponent } from './controls/switch/switch.component';
import { FileUploadOssComponent } from './controls/file-upload-oss/file-upload-oss.component';
import { CarTypeComponent } from './controls/car-type/car-type.component';

import { NgxQueryDynamicComponent } from './components/ngx-query-dynamic/ngx-query-dynamic.component';

import { TextBoxPropertyEditorComponent } from './property-editors/text-box-property-editor/text-box-property-editor.component';
import { WidthEditorComponent } from './property-editors/width-editor/width-editor.component';
import { OptionPropertyEditorComponent } from './property-editors/option-property-editor/option-property-editor.component';
import { MultiTextBoxPropertyEditorComponent } from './property-editors/multi-text-box-property-editor/multi-text-box-property-editor.component';
import { CheckBoxPropertyEditorComponent } from './property-editors/check-box-property-editor/check-box-property-editor.component';
import { DropDownPropertyEditorComponent } from './property-editors/drop-down-property-editor/drop-down-property-editor.component';
import { NumberPropertyEditorComponent } from './property-editors/number-property-editor/number-property-editor.component';
import { QueryConditionEditorComponent } from './property-editors/query-condition-editor/query-condition-editor.component';

import { RequiredValidatorEditorComponent } from './validators/editors/required-validator-editor/required-validator-editor.component';
import { UniqueValidatorEditorComponent } from './validators/editors/unique-validator-editor/unique-validator-editor.component';
import { StringLengthValidatorEditorComponent } from './validators/editors/string-length-validator-editor/string-length-validator-editor.component';
import { RegexValidatorEditorComponent } from './validators/editors/regex-validator-editor/regex-validator-editor.component';
import { DigitalRangeValidatorEditorComponent } from './validators/editors/digital-range-validator-editor/digital-range-validator-editor.component';
import { DigitalTypeValidatorEditorComponent } from './validators/editors/digital-type-validator-editor/digital-type-validator-editor.component';
// tslint:disable-next-line: max-line-length
import { FileUploadFileTypeValidatorEditorComponent } from './validators/editors/file-upload-file-type-validator-editor/file-upload-file-type-validator-editor.component';
// tslint:disable-next-line: max-line-length
import { FileUploadFileSizeValidatorEditorComponent } from './validators/editors/file-upload-file-size-validator-editor/file-upload-file-size-validator-editor.component';
import { ValidatorEditorTemplateComponent } from './validators/editors/validator-editor-template/validator-editor-template.component';
import { VehicleSelectionComponent } from './controls/vehicle-selection/vehicle-selection.component';
import { RegionSelectionComponent } from './controls/region-selection/region-selection.component';
import { RegionComponent } from './controls/region/region.component';
import { ChildFormControlComponent } from './controls/child-form-control/child-form-control.component';

import { ToFixedPipe } from './utils/to-fixed.pipe';
import { SelectOptionsDirective } from './directive/select-options.directive';
import { NgxQueryDisableInputDirective } from './directive/ngx-query-disable-input.directive';
import { NgxDataTableDynamicComponent } from './components/ngx-data-table-dynamic/ngx-data-table-dynamic.component';
import { ControlTranslateService } from './services/control-translate.service';

import { DragService } from './services/drag.service';
import { ControlValidatorService } from './services/control-validator.service';
import { DrawToolService } from './tools/draw-tool.service';
import { DragDrawToolService } from './tools/drag-draw-tool.service';
import { DefaultValuePropertyEditorComponent } from './property-editors/default-value-property-editor/default-value-property-editor.component';
import { CascadePropertyEditorComponent } from './property-editors/cascade-property-editor/cascade-property-editor.component';
import { SelectConditionPropertyEditorComponent } from './property-editors/select-condition-property-editor/select-condition-property-editor.component';
import { ShowConditionPropertyEditorComponent } from './property-editors/show-condition-property-editor/show-condition-property-editor.component';
import { UnionCheckValidatorEditorComponent } from './validators/editors/union-check-validator-editor/union-check-validator-editor.component';
import { ShowHiddenListComponent } from './components/show-hidden/show-hidden-list/show-hidden-list.component';
import { ShowHiddenUiComponent } from './components/show-hidden/show-hidden-ui/show-hidden-ui.component';
import { ShowHiddenEntryComponent } from './components/show-hidden/show-hidden-entry/show-hidden-entry.component';
import { EntityPropertyEditorComponent } from './property-editors/entity-property-editor/entity-property-editor.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CalendarModule,
    DropdownModule,
    MultiSelectModule,
    ZrSwitchModule,
    ZrCounterModule,
    NgxQueryModule,
    BsDatepickerModule,
    NgxDatatableModule,
    SweetAlert2Module,
    SelectModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  declarations: [
    DynamicFormComponent,
    DynamicFormDesignerComponent,
    LayoutDesignerComponent,
    LabelComponent,
    LayoutHostDirective,
    TextBoxComponent,
    RowComponent,
    RegionComponent,
    CellComponent,
    ChildFormControlComponent,

    DesignHostComponent,
    TextBoxPropertyEditorComponent,
    PropertyGridComponent,
    PropertyGroupComponent,
    CheckBoxPropertyEditorComponent,
    ToolBoxComponent,
    ToolBoxGroupComponent,
    ContainerComponent,
    ToolBoxControlComponent,
    AbstractControlContainerComponent,
    DropDownPropertyEditorComponent,

    DragDirective,
    DatetimeComponent,
    RegionSelectionComponent,
    MultiLineTextBoxComponent,
    RadioComponent,
    RadioComponent,
    MultiCheckBoxComponent,
    DropDownComponent,
    OptionsControlBaseComponent,
    VehicleSelectionComponent,
    DigitalComponent,
    ParagraphComponent,
    EmailComponent,
    SwitchComponent,
    CarTypeComponent,
    FileUploadOssComponent,
    ToFixedPipe,
    WidthEditorComponent,
    ElementClassDirective,
    OptionPropertyEditorComponent,
    MultiTextBoxPropertyEditorComponent,
    EventGroupComponent,
    ValidatorGroupComponent,
    FormEventGroupComponent,
    ControlIdDirective,
    RequiredValidatorEditorComponent,
    ValidateMessageComponent,
    UniqueValidatorEditorComponent,
    StringLengthValidatorEditorComponent,
    DigitOnlyDirective,
    RegexValidatorEditorComponent,
    DigitalRangeValidatorEditorComponent,
    DigitalTypeValidatorEditorComponent,
    FileUploadFileTypeValidatorEditorComponent,
    FileUploadFileSizeValidatorEditorComponent,
    ControlValidateLabelComponent,
    DragBoxComponent,
    NumberPropertyEditorComponent,
    QueryConditionEditorComponent,

    FloatDigitOnlyDirective,
    ControlValidateDirective,
    SelectOptionsDirective,

    NgxQueryDynamicComponent,
    NgxQueryDisableInputDirective,
    NgxDataTableDynamicComponent,
    ValidatorEditorTemplateComponent,
    DefaultValuePropertyEditorComponent,
    CascadePropertyEditorComponent,
    SelectConditionPropertyEditorComponent,
    ShowConditionPropertyEditorComponent,
    UnionCheckValidatorEditorComponent,
    ShowHiddenListComponent,
    ShowHiddenUiComponent,
    ShowHiddenEntryComponent,
    EntityPropertyEditorComponent
  ],
  exports: [
    DynamicFormComponent,
    DynamicFormDesignerComponent,
    LayoutDesignerComponent,
    LabelComponent,
    ContainerComponent,
    ControlValidateLabelComponent,
    ValidateMessageComponent,
    NgxQueryDynamicComponent,
    NgxDataTableDynamicComponent,

    DesignHostComponent,

    LayoutHostDirective,
    DigitOnlyDirective,
    FloatDigitOnlyDirective,
    ControlIdDirective,
    ControlValidateDirective,
    ElementClassDirective,
    SelectOptionsDirective,
    ValidatorEditorTemplateComponent,

    TextBoxPropertyEditorComponent,
    CheckBoxPropertyEditorComponent,
    DropDownPropertyEditorComponent,
    WidthEditorComponent,
    OptionPropertyEditorComponent,
    MultiTextBoxPropertyEditorComponent,
    NumberPropertyEditorComponent,
    QueryConditionEditorComponent,
    DefaultValuePropertyEditorComponent,
    CascadePropertyEditorComponent,
    EntityPropertyEditorComponent
  ],
  entryComponents: [
    LabelComponent,
    TextBoxComponent,
    MultiLineTextBoxComponent,
    RadioComponent,
    MultiCheckBoxComponent,
    DropDownComponent,
    DigitalComponent,
    DatetimeComponent,
    ParagraphComponent,
    EmailComponent,
    RegionComponent,
    CarTypeComponent,
    SwitchComponent,
    FileUploadOssComponent,
    RegionSelectionComponent,
    ChildFormControlComponent,

    DesignHostComponent,
    ContainerComponent,
    RowComponent,
    CellComponent,

    TextBoxPropertyEditorComponent,
    CheckBoxPropertyEditorComponent,
    DropDownPropertyEditorComponent,
    WidthEditorComponent,
    OptionPropertyEditorComponent,
    MultiTextBoxPropertyEditorComponent,
    NumberPropertyEditorComponent,
    QueryConditionEditorComponent,
    DefaultValuePropertyEditorComponent,
    CascadePropertyEditorComponent,
    SelectConditionPropertyEditorComponent,
    ShowConditionPropertyEditorComponent,
    EntityPropertyEditorComponent,

    ToolBoxComponent,
    ToolBoxGroupComponent,
    ToolBoxControlComponent,
    DynamicFormComponent,
    DynamicFormDesignerComponent,
    FormEventGroupComponent,

    ValidatorGroupComponent,
    RequiredValidatorEditorComponent,
    UniqueValidatorEditorComponent,
    StringLengthValidatorEditorComponent,
    RegexValidatorEditorComponent,
    DigitalRangeValidatorEditorComponent,
    DigitalTypeValidatorEditorComponent,
    FileUploadFileTypeValidatorEditorComponent,
    FileUploadFileSizeValidatorEditorComponent,
    UnionCheckValidatorEditorComponent,

    ShowHiddenListComponent,
    ShowHiddenUiComponent
  ],
  providers: [
    // è¿éå¿é¡»è¦å ä¸ï¼å¦åä¼æ¥æé å¨æç»ä»¶æ¾ä¸å°
    ControlTranslateService,
    ControlValidatorService,
    DragService,
    // DesignerConfigurationService,
    // SpecificationHelperService,
    DrawToolService,
    DragDrawToolService
  ]
})
export class DynamicFormDesignerModule {}
